/**
 * Rail de acceso rápido del POS: vistas inteligentes (Favoritos / Combos /
 * En oferta / Paquetes) + categorías en una sola hilera single-select.
 * Todo client-side sobre el snapshot en RAM — sin cambios de backend.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  checkPromotionConditions,
  promotionMatchesLine,
  type PricedCartLine,
  type PromotionChannel,
  type PromotionWithTargets,
} from '../../promotions/public-api';
import type { CategoryOption, ProductSearchRow } from '../../catalog/domain-api';

export type PosRailCounts = {
  favorites: number;
  combos: number;
  promos: number;
  packages: number;
};

/**
 * ¿Mostrar control de categoría en el rail?
 * Desktop: no — categoría solo vía Filtros → Categoría.
 * Mobile: sí — chip «Categoría · …» abre el selector.
 */
export function posRailShowsCategoryControl(layoutWide: boolean, categoryCount: number): boolean {
  if (categoryCount <= 0) return false;
  return !layoutWide;
}

/** Chips inteligentes visibles (desk oculta fila si solo queda «Todos»). */
export function posQuickAccessRailShowChipRow(
  layoutWide: boolean,
  counts: PosRailCounts,
  showCategoryOnRail: boolean,
): boolean {
  let smartCount = 1;
  if (counts.favorites > 0) smartCount += 1;
  if (counts.combos > 0) smartCount += 1;
  if (counts.promos > 0) smartCount += 1;
  if (counts.packages > 0) smartCount += 1;
  const categoryChips = showCategoryOnRail ? 1 : 0;
  const onlyDefaultSmartChip = smartCount === 1 && categoryChips === 0;
  return (smartCount > 0 || categoryChips > 0) && !(layoutWide && onlyDefaultSmartChip);
}

function posDeskCatalogRailChipRowVisible(input: {
  layoutWide: boolean;
  railCounts: PosRailCounts | null | undefined;
  categoryOptions: CategoryOption[];
  hasCategoryPicker: boolean;
}): boolean {
  if (!input.railCounts) return false;
  const leafCount = input.categoryOptions.filter((c) => {
    if (c.id === 'all') return false;
    if (input.railCounts!.combos > 0 && c.label.trim().toLowerCase() === 'combos') return false;
    return true;
  }).length;
  const showCategoryOnRail =
    input.hasCategoryPicker && posRailShowsCategoryControl(input.layoutWide, leafCount);
  return posQuickAccessRailShowChipRow(input.layoutWide, input.railCounts, showCategoryOnRail);
}

/** Desk: hay algo que mostrar bajo el buscador (fulfillment y/o rail). */
export function posDeskCatalogChromeVisible(input: {
  showFulfillmentToggle: boolean;
  layoutWide: boolean;
  railCounts: PosRailCounts | null | undefined;
  categoryOptions: CategoryOption[];
  hasCategoryPicker: boolean;
}): boolean {
  if (!input.layoutWide) return false;
  if (input.showFulfillmentToggle) return true;
  return posDeskCatalogRailChipRowVisible(input);
}

/** FlatList listHeader en desk: mismo criterio que el bloque chrome renderizado. */
export function posDeskCatalogListHeaderActive(input: {
  showFulfillmentToggle: boolean;
  layoutWide: boolean;
  railCounts: PosRailCounts | null | undefined;
  categoryOptions: CategoryOption[];
  hasCategoryPicker: boolean;
}): boolean {
  if (!input.layoutWide) return false;
  if (input.showFulfillmentToggle) return true;
  return posDeskCatalogRailChipRowVisible(input);
}

export type PosRailView = 'all' | 'favorites' | 'combos' | 'promos' | 'packages';

/** Selección del rail: vista inteligente o categoría (`cat:<id>`). */
export type PosRailSelection = PosRailView | `cat:${string}`;

export function parseRailSelection(sel: string): PosRailSelection {
  if (sel.startsWith('cat:')) return sel as PosRailSelection;
  if (sel === 'favorites' || sel === 'combos' || sel === 'promos' || sel === 'packages') return sel;
  return 'all';
}

// ─── Favoritos (por sucursal, en el dispositivo — decisión server-side queda en #1442) ──

const favKey = (branchId: string) => `pos.favorites.v1.${branchId}`;

export async function loadPosFavorites(branchId: string): Promise<Set<string>> {
  try {
    const raw = await AsyncStorage.getItem(favKey(branchId));
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

export async function savePosFavorites(branchId: string, ids: Set<string>): Promise<void> {
  try {
    await AsyncStorage.setItem(favKey(branchId), JSON.stringify([...ids]));
  } catch {
    // non-fatal
  }
}

// ─── Resolución de sets por vista ───────────────────────────────────────────

/** Promo vigente ahora para el canal POS y la sucursal (solo fechas + estado). */
export function promoIsLiveNow(
  p: Pick<PromotionWithTargets, 'isActive' | 'deletedAt' | 'startsAt' | 'endsAt' | 'channels' | 'branchIds'>,
  branchId: string | null | undefined,
  now = new Date(),
): boolean {
  if (!p.isActive || p.deletedAt) return false;
  const channels: PromotionChannel[] | null | undefined = p.channels;
  if (channels && channels.length > 0 && !channels.includes('pos')) return false;
  if (branchId && p.branchIds && p.branchIds.length > 0 && !p.branchIds.includes(branchId)) return false;
  if (p.startsAt && now < new Date(p.startsAt)) return false;
  if (p.endsAt && now > new Date(p.endsAt)) return false;
  return true;
}

function catalogLineFromRow(
  row: Pick<ProductSearchRow, 'id' | 'categoryId' | 'brandId'>,
): PricedCartLine {
  return {
    lineId: String(row.id),
    productId: String(row.id),
    quantity: 1,
    unitPrice: 1,
    categoryId: row.categoryId ?? null,
    brandId: row.brandId ?? null,
    variantId: null,
    itemType: 'product',
    isMisc: false,
  };
}

/**
 * Ids de productos alcanzados por alguna promo POS vigente **por ítem** (badge / chip En oferta).
 * Alineado con portal: no incluye promos de carrito ni códigos manuales — esas se ven en caja / banner.
 * Usa `promotionMatchesLine` + condiciones de horario/día (sin mínimos de carrito).
 */
export function resolvePromoProductIds(
  promotions: PromotionWithTargets[],
  rows: Array<Pick<ProductSearchRow, 'id' | 'categoryId' | 'brandId'>>,
  variantToProductId: Map<string, string>,
  branchId: string | null | undefined,
  now = new Date(),
  timezoneOffsetMinutes?: number,
): Set<string> {
  const out = new Set<string>();
  const live = promotions.filter((p) => promoIsLiveNow(p, branchId, now));
  if (live.length === 0) return out;

  for (const promo of live) {
    if (promo.scope === 'cart') continue;
    if (promo.applyMode !== 'automatic') continue;
    if (!checkPromotionConditions(promo, [], now, undefined, timezoneOffsetMinutes)) continue;

    const targets = promo.targets ?? [];
    for (const row of rows) {
      const line = catalogLineFromRow(row);
      if (promotionMatchesLine(promo, targets, line)) out.add(String(row.id));
    }
    for (const t of targets) {
      if (t.targetType !== 'variant') continue;
      const pid = variantToProductId.get(String(t.targetId));
      if (pid) out.add(pid);
    }
  }
  return out;
}

/** Set final para la vista activa; `null` = sin allowlist (vista Todos/paquetes/categoría). */
export function railProductIdsForView(
  view: PosRailView,
  sets: { favorites: Set<string>; combos: Set<string>; promos: Set<string> },
): ReadonlySet<string> | null {
  if (view === 'favorites') return sets.favorites;
  if (view === 'combos') return sets.combos;
  if (view === 'promos') return sets.promos;
  return null;
}
